import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  Modifier,
  UniqueIdentifier,
  closestCenter,
  defaultDropAnimation,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSelector } from '@xstate/store/react';
import capitalize from 'lodash/capitalize';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLatest, useUnmount } from 'react-use';

import { usePreventAutoReloadOnNewVersion } from '@eluve/blocks';
import {
  DebugJson,
  Divider,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  FormItem,
  HStack,
  Heading,
  Input,
  Label,
  NewButton,
  Paragraph,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@eluve/components';
import { LlmOutputTypesEnum, LlmOutputTypesLookup } from '@eluve/graphql-types';
import {
  LLM_OUTPUT_TYPE_VARIABLES,
  convertDynamicArtifactToJsonSchema,
} from '@eluve/llm-outputs';

import { BlockPicker } from './BlockPicker';
import { SortableTreeItem } from './SortableTreeItem';
import { templateBuilderStore } from './templateBuilderStore';
import { useIsStateDirty } from './useIsStateDirty';
import { areAllBlocksValid, flattenBlocks, getProjection } from './utilities';

type ValidationError = { message: string; field?: 'name' | 'description' };

const adjustTranslate: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: transform.y - 25,
  };
};

const dropAnimationConfig: DropAnimation = {
  keyframes({ transform }) {
    return [
      { opacity: 1, transform: CSS.Transform.toString(transform.initial) },
      {
        opacity: 0,
        transform: CSS.Transform.toString({
          ...transform.final,
          x: transform.final.x + 5,
          y: transform.final.y + 5,
        }),
      },
    ];
  },
  easing: 'ease-out',
  sideEffects({ active }) {
    active.node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: defaultDropAnimation.duration,
      easing: defaultDropAnimation.easing,
    });
  },
};

const PREVENT_RELOAD_REASON = 'template-dirty';

export const DynamicArtifactTemplateBuilder: React.FC<{
  onIsValidChange?: (isValid: boolean) => void;
  isReadOnly?: boolean;
  showJsonSchema?: boolean;
  hideTemplateDetails?: boolean;
}> = ({
  onIsValidChange,
  isReadOnly = false,
  showJsonSchema = false,
  hideTemplateDetails = false,
}) => {
  const toast = useToast();
  const indentationWidth = 24;
  const indicator = true;
  const isValid = useRef(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const onIsValidChangeRef = useLatest(onIsValidChange);

  useEffect(() => {
    templateBuilderStore.send({
      type: 'setIsReadOnly',
      isReadOnly,
    });
  }, [isReadOnly]);

  const blocks = useSelector(
    templateBuilderStore,
    (state) => state.context.blocks,
  );

  const outputType = useSelector(
    templateBuilderStore,
    (state) => state.context.outputType,
  );

  const name = useSelector(
    templateBuilderStore,
    ({ context: { name } }) => name,
  );

  const description = useSelector(
    templateBuilderStore,
    ({ context: { description } }) => description,
  );

  const variantNotes = useSelector(
    templateBuilderStore,
    ({ context: { variantNotes } }) => variantNotes,
  );

  const isImported = useSelector(
    templateBuilderStore,
    ({ context: { isImported } }) => isImported,
  );

  const exportableJson = useSelector(
    templateBuilderStore,
    ({ context: { exportableJson } }) => exportableJson,
  );

  const isDirty = useIsStateDirty();

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [offsetLeft, setOffsetLeft] = useState(0);

  const resetState = () => {
    setOverId(null);
    setActiveId(null);
    setOffsetLeft(0);

    document.body.style.setProperty('cursor', '');
  };

  const flattenedBlocks = useMemo(() => flattenBlocks(blocks), [blocks]);

  useEffect(() => {
    const subscription = templateBuilderStore.subscribe((state) => {
      const { context } = state;
      const validationErrors: ValidationError[] = [];

      const allBlocksValid = areAllBlocksValid(context.blocks);
      const flatBlocks = flattenBlocks(context.blocks);
      if (!allBlocksValid) {
        validationErrors.push({
          message: 'All blocks must be valid',
        });
      }

      const hasAtLeastOneNonGroupBlock =
        flatBlocks.length > 0 && flatBlocks.some((b) => b.type !== 'group');

      if (!hasAtLeastOneNonGroupBlock) {
        validationErrors.push({
          message: 'At least one non-group block is required',
        });
      }

      if (!hideTemplateDetails && !context.name) {
        validationErrors.push({
          message: 'Required',
          field: 'name',
        });
      }

      if (!hideTemplateDetails && !context.description) {
        validationErrors.push({
          message: 'Required',
          field: 'description',
        });
      }

      const isTemplateValid = validationErrors.length === 0;

      setValidationErrors(validationErrors);

      if (isValid.current !== isTemplateValid) {
        isValid.current = isTemplateValid;
        onIsValidChangeRef.current?.(isTemplateValid);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [hideTemplateDetails, onIsValidChangeRef]);

  // Prevent navigation/reload when template has unsaved changes
  // This combines version checker prevention and browser navigation prevention
  useEffect(() => {
    const shouldPrevent = isDirty && !isReadOnly;

    if (shouldPrevent) {
      usePreventAutoReloadOnNewVersion
        .getState()
        .addReason(PREVENT_RELOAD_REASON);
    } else {
      usePreventAutoReloadOnNewVersion
        .getState()
        .removeReason(PREVENT_RELOAD_REASON);
    }

    if (!shouldPrevent) {
      return;
    }

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [isDirty, isReadOnly]);

  useUnmount(() => {
    usePreventAutoReloadOnNewVersion
      .getState()
      .removeReason(PREVENT_RELOAD_REASON);
  });

  const templateNameHasErrors =
    validationErrors.find((e) => e.field === 'name') && !isReadOnly;
  const templateDescriptionHasErrors =
    validationErrors.find((e) => e.field === 'description') && !isReadOnly;

  const projected =
    activeId && overId
      ? getProjection(
          flattenedBlocks,
          activeId,
          overId,
          offsetLeft,
          indentationWidth,
        )
      : null;

  const sortedIds = useMemo(
    () => flattenedBlocks.map((block) => block.id),
    [flattenedBlocks],
  );

  const activeBlock = activeId
    ? flattenedBlocks.find((b) => b.id === activeId)
    : null;

  const handleDragStart = (evt: DragStartEvent) => {
    const {
      active: { id },
    } = evt;
    setActiveId(id);
    setOverId(id);
    document.body.style.setProperty('cursor', 'grabbing');
  };

  const handleDragEnd = (evt: DragEndEvent) => {
    const { over, active } = evt;
    resetState();

    if (projected && over) {
      const { parentId } = projected;
      const movedBlock = flattenedBlocks.find((b) => b.id === active.id);
      if (!movedBlock) {
        return;
      }

      if (parentId === movedBlock.parentId) {
        templateBuilderStore.send({
          type: 'moveInsideGroup',
          parentId,
          activeId: active.id,
          overId: over.id,
        });
      } else {
        templateBuilderStore.send({
          type: 'moveBlockBetweenGroups',
          from: movedBlock.parentId,
          to: {
            id: parentId,
            activeId: active.id,
            overId: over.id,
          },
        });
      }
    }
  };

  const handleDragOver = (evt: DragOverEvent) => {
    setOverId(evt?.over?.id ?? null);
  };

  const handleDragMove = (evt: DragMoveEvent) => {
    setOffsetLeft(evt.delta.x);
  };

  const jsonSchema = convertDynamicArtifactToJsonSchema({
    name,
    description,
    blocks,
  }).valueOf();

  return (
    <VStack className="min-w-0" gap={4}>
      <HStack justify="between">
        <Heading size="sm">About This Template</Heading>
        <HStack wFull={false}>
          {isReadOnly && exportableJson && (
            <Drawer>
              <DrawerTrigger asChild>
                <NewButton
                  type="outline"
                  icon={{ name: 'ClipboardCopy' }}
                  text="Export"
                />
              </DrawerTrigger>
              <DrawerContent>
                <VStack gap={2} className="container h-[80vh] overflow-auto">
                  <NewButton
                    onClick={async () => {
                      await navigator.clipboard.writeText(exportableJson);
                      toast.success('Copied JSON schema to clipboard');
                    }}
                    icon={{ name: 'ClipboardCopy' }}
                    text="Copy JSON Schema"
                  />
                  <Textarea
                    value={exportableJson}
                    className="w-full grow font-mono shadow"
                    placeholder="Paste JSON schema here"
                    readOnly
                  />
                </VStack>
              </DrawerContent>
            </Drawer>
          )}
        </HStack>
      </HStack>
      <VStack gap={4}>
        <FormItem className="w-full">
          <Label htmlFor="template-name">Name</Label>
          <Input
            value={name}
            disabled={
              isReadOnly || (isImported ?? false) || hideTemplateDetails
            }
            onChange={(e) => {
              templateBuilderStore.send({
                type: 'setName',
                name: e.target.value,
              });
            }}
            placeholder="Name"
            id="template-name"
          />
          {templateNameHasErrors && (
            <Paragraph className="text-text-negative" color="inherit" size="md">
              {validationErrors.find((e) => e.field === 'name')?.message}
            </Paragraph>
          )}
        </FormItem>

        <FormItem className="w-full">
          <Label htmlFor="output-type">Type</Label>
          <Select
            value={outputType}
            disabled={
              isReadOnly || (isImported ?? false) || hideTemplateDetails
            }
            onValueChange={(val) => {
              templateBuilderStore.send({
                type: 'setOutputType',
                outputType: val as LlmOutputTypesEnum,
              });
            }}
          >
            <SelectTrigger id="output-type">
              <SelectValue placeholder="Output Type" />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(LLM_OUTPUT_TYPE_VARIABLES)
                .filter(
                  (k) =>
                    k !== LlmOutputTypesLookup.DYNAMIC_OUTPUT &&
                    k !== LlmOutputTypesLookup.TRANSLATION,
                )
                .sort()
                .map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.split('_').map(capitalize).join(' ')}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </FormItem>

        <FormItem className="w-full">
          <Label htmlFor="template-description">Description</Label>
          <Textarea
            id="template-description"
            value={description}
            disabled={isReadOnly || hideTemplateDetails}
            onChange={(e) => {
              templateBuilderStore.send({
                type: 'setDescription',
                description: e.target.value,
              });
            }}
            placeholder="Provide a description of what the artifact produced by this template should be."
          />
          {templateDescriptionHasErrors && (
            <Paragraph className="text-text-negative" color="inherit" size="md">
              {validationErrors.find((e) => e.field === 'description')?.message}
            </Paragraph>
          )}
        </FormItem>
      </VStack>
      <Divider className="my-0" />
      <VStack>
        <Heading size="sm">Variant Notes</Heading>
        <Text size="sm" color="secondary">
          Internal notes for this template variant. These are visible to
          template editors and will not appear in generated content.
        </Text>
        <FormItem className="w-full">
          <Textarea
            value={variantNotes ?? ''}
            disabled={isReadOnly}
            onChange={(e) => {
              templateBuilderStore.send({
                type: 'setVariantNotes',
                variantNotes: e.target.value,
              });
            }}
            placeholder="Add notes about this variant..."
            className="min-h-[80px]"
          />
        </FormItem>
      </VStack>
      <Divider className="my-0" />
      <HStack justify="between">
        <Heading size="sm">Blocks</Heading>
        <HStack wFull={false}>
          {Boolean(flattenedBlocks.length > 0) && (
            <>
              <NewButton
                icon={{ name: 'ChevronsDownUp' }}
                onClick={() =>
                  templateBuilderStore.send({
                    type: 'toggleCollapseAll',
                    collapsed: true,
                  })
                }
                text="Collapse All"
                type="outline"
              />
              <NewButton
                icon={{ name: 'ChevronsUpDown' }}
                onClick={() =>
                  templateBuilderStore.send({
                    type: 'toggleCollapseAll',
                    collapsed: false,
                  })
                }
                text="Expand All"
                type="outline"
              />
            </>
          )}
          {showJsonSchema && (
            <Drawer>
              <DrawerTrigger asChild>
                <NewButton
                  icon={{ name: 'Braces' }}
                  text="View JSON"
                  type="outline"
                />
              </DrawerTrigger>
              <DrawerContent>
                <VStack className="container h-[75vh]" gap={4}>
                  <HStack justify="between">
                    <Heading size="lg">Template JSON</Heading>
                    <NewButton
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            JSON.stringify(jsonSchema),
                          );
                          toast.success('Template JSON copied to clipboard.');
                        } catch {
                          toast.error('Template JSON could not be copied.');
                        }
                      }}
                      icon={{ name: 'Copy' }}
                      text="Copy to clipboard"
                    />
                  </HStack>
                  <DebugJson value={jsonSchema} />
                </VStack>
              </DrawerContent>
            </Drawer>
          )}
        </HStack>
      </HStack>
      <VStack gap={6}>
        <DndContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragMove={handleDragMove}
          onDragCancel={resetState}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={sortedIds}
            strategy={verticalListSortingStrategy}
          >
            {flattenedBlocks.map((block) => {
              const { id, collapsed, depth } = block;
              return (
                <SortableTreeItem
                  key={id}
                  id={id}
                  value={block}
                  depth={id === activeId && projected ? projected.depth : depth}
                  indentationWidth={indentationWidth}
                  indicator={indicator}
                  collapsed={Boolean(collapsed)}
                />
              );
            })}
            {createPortal(
              <DragOverlay
                modifiers={indicator ? [adjustTranslate] : undefined}
                dropAnimation={dropAnimationConfig}
              >
                {activeId && activeBlock ? (
                  <SortableTreeItem
                    id={activeId}
                    depth={activeBlock.depth}
                    value={activeBlock}
                    clone
                    indentationWidth={indentationWidth}
                    collapsed={true}
                  />
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </SortableContext>
        </DndContext>
        {flattenedBlocks.length ? <Divider /> : null}
        {!isReadOnly && !isImported && (
          <VStack gap={3}>
            <Text size="md" weight="medium">
              Add Block
            </Text>
            <BlockPicker parentId={null} showTips />
          </VStack>
        )}
      </VStack>
    </VStack>
  );
};
